import { createApi } from '@reduxjs/toolkit/dist/query/react'
import { IAdvisorDetailResult } from 'api/advisor.types'
import { IHoliday, INfsProfile, IRelatedParty } from 'api/datahub'
import { constructOdataQuery, escapeFilterString } from 'api/odata'
import { IOdataRequest } from 'api/odata.types'
import { escapeAndEncodeQuery, tokenizeQuery } from 'api/search'
import { isString } from 'lodash'
import { flow } from 'lodash/fp'
import { IOdataResult } from 'shared/contracts/IOdataResult'
import { fetchAllPagedOdataResults } from 'shared/export/api'
import { tryAcquireAccessToken } from 'shared/services/auth'
import { AppState } from 'store/shared'
import { getRockefellerApiConfig } from 'store/system'
import { arrayCommaParamsSerializer, axiosBaseQuery } from '../shared'
import {
  IEnhancedClientProfileResponse,
  INfsProfileWithPilotInfo
} from './IEnhancedClientProfile'
import {
  INetNewMoneyMonthSummary,
  INetNewMoneyMonthSummaryRequest
} from './INetNewMoney'

export const DatahubApiSliceKey = 'api.datahub'
export type DatahubApiReducerState = {
  [DatahubApiSliceKey]: ReturnType<typeof datahubApi.reducer>
}

const getDatahubApiBaseUrl = (state: AppState) => {
  const base = flow(getRockefellerApiConfig, (x) => x?.root)(state)
  const v1 = new URL('datahub', base)
  return v1.href
}

const getDatahubApiAuthToken = (state: AppState) => {
  const scopes = flow(getRockefellerApiConfig, (x) => x?.scopes)(state)

  if (!scopes) {
    throw new Error('')
  }

  return tryAcquireAccessToken(scopes)
}

export const constructQuery = (query: string) =>
  tokenizeQuery(query)
    .map(escapeFilterString)
    .map(escapeAndEncodeQuery)
    .filter(Boolean)
    .map((x) => `${x}*`)
    .join(' AND ')

export const datahubApi = createApi({
  reducerPath: DatahubApiSliceKey,
  baseQuery: axiosBaseQuery({
    getBaseUrl: (state) => getDatahubApiBaseUrl(state),
    getAuthToken: (state) => getDatahubApiAuthToken(state)
  }),
  tagTypes: ['profile', 'performancelogs'],
  endpoints: (builder) => ({
    fetchNfsProfiles: builder.query<INfsProfile[] | undefined, IOdataRequest>({
      query: (req) => ({
        url: 'nfsProfiles?' + constructOdataQuery(req)
      }),
      transformResponse: (response: IOdataResult<INfsProfile>) =>
        response?.value
    }),
    fetchNfsProfilesByPartyId: builder.query<INfsProfile[] | undefined, string>(
      {
        query: (id) => ({
          url: 'nfsProfiles',
          params: {
            $filter: `Partyid eq '${id}'`,
            select: [
              'id',
              'hasAnyMfaMethods',
              'isEnrolledInEauth',
              'lastlogindate',
              'loginid',
              'wsportaluserid',
              'accounts'
            ]
          },
          paramsSerializer: arrayCommaParamsSerializer,
          headers: { 'api-version': '1.0' }
        }),
        transformResponse: (response: IOdataResult<INfsProfile>) =>
          response?.value,
        providesTags: (_result, _error, id) => [{ type: 'profile', id }]
      }
    ),
    fetchNfsProfileById: builder.query<
      INfsProfile | undefined,
      string | { id: string | number; select?: string[] }
    >({
      query: (args) => {
        const { id, select } = isString(args)
          ? { id: args, select: undefined }
          : args
        return {
          url: `/nfsProfiles/${id}`,
          params: { select },
          paramsSerializer: arrayCommaParamsSerializer,
          headers: { 'api-version': '1.0' }
        }
      },
      providesTags: (result) => [{ type: 'profile', id: result?.id }]
    }),
    fetchEnhancedProfileByWealthscapeId: builder.query<
      IEnhancedClientProfileResponse | undefined,
      string
    >({
      query: (wsPortalUserId) => ({
        method: 'POST',
        url: `/nfsProfiles/GetEnhancedProfile`,
        data: {
          request: {
            wsPortalUserId: wsPortalUserId
          }
        },
        headers: { 'api-version': '1.0' }
      }),
      providesTags: (result) => [{ type: 'profile', id: result?.profile.id }]
    }),
    fetchClientsPilotInfo: builder.query<
      INfsProfileWithPilotInfo[] | undefined,
      number[]
    >({
      query: (ids) => ({
        method: 'GET',
        url: `/nfsProfiles`,
        params: {
          $filter: `id in (${ids.join(',')})`,
          $select: 'multiCustodianPilotInfo'
        },
        headers: { 'api-version': '1.0' }
      }),
      transformResponse: (response: IOdataResult<INfsProfileWithPilotInfo>) =>
        response?.value
    }),
    getRelatedPartiesByHouseholdId: builder.query<
      IRelatedParty[] | undefined,
      string
    >({
      query: (householdId) => ({
        url: `/relatedparties/Household/${householdId}`
      }),
      keepUnusedDataFor: 60 * 20
    }),
    getRelatedPartiesByAccountId: builder.query<
      IRelatedParty[] | undefined,
      string
    >({
      query: (accountId) => ({
        url: `/relatedparties/Account/${accountId}`
      }),
      keepUnusedDataFor: 60 * 20
    }),
    getRelatedPartiesById: builder.query<
      IRelatedParty[] | undefined,
      { id: string; type: 'account' | 'household' | 'party' }
    >({
      query: ({ id, type }) => `/relatedparties/${type}/${id}`,
      keepUnusedDataFor: 60 * 20
    }),
    getMarketHolidays: builder.query<IHoliday[] | undefined, void>({
      query: () => ({
        url: 'MarketHolidays'
      }),
      transformResponse: (response: IOdataResult<IHoliday>) => response?.value,
      keepUnusedDataFor: 60 * 20
    }),
    getNetNewMoneyMonthSummary: builder.query<
      INetNewMoneyMonthSummary[] | undefined,
      INetNewMoneyMonthSummaryRequest
    >({
      query: (request) => ({
        url: 'NetNewMoneyMonthSummary',
        method: 'POST',
        data: request
      })
    }),
    getDomain: builder.query<IAdvisorDetailResult[] | undefined, void>({
      queryFn: async (arg, api, extraOptions, baseQuery) => {
        const baseRequest: IOdataRequest = {
          select: [
            'ClientAdvisorID',
            'ClientAdvisor',
            'id',
            'ClientAdvisorTeam',
            'AdvisorKPI/poolMemberKPI',
            'BusinessSegment',
            'RegionName',
            'HubName'
          ],

          orderby: [{ dataPath: 'id', direction: 'desc' }]
        }

        const fetchDomain = async (request: IOdataRequest) => {
          const result = await baseQuery(
            `search/advisors?${constructOdataQuery(request)}`
          )
          if (result.error) {
            throw result.error
          }
          return result.data as IOdataResult<IAdvisorDetailResult>
        }

        try {
          const results = await fetchAllPagedOdataResults(
            baseRequest,
            fetchDomain
          )

          return { data: results }
        } catch (error) {
          return { error }
        }
      }
    })
  })
})

export const {
  useFetchNfsProfileByIdQuery,
  useFetchNfsProfilesQuery,
  useLazyFetchNfsProfilesQuery,
  useFetchNfsProfilesByPartyIdQuery,
  useLazyFetchNfsProfileByIdQuery,
  useFetchEnhancedProfileByWealthscapeIdQuery,
  useFetchClientsPilotInfoQuery,
  useGetRelatedPartiesByHouseholdIdQuery,
  useGetRelatedPartiesByAccountIdQuery,
  useGetRelatedPartiesByIdQuery,
  useGetMarketHolidaysQuery,
  useGetDomainQuery,
  useGetNetNewMoneyMonthSummaryQuery,
  useLazyGetNetNewMoneyMonthSummaryQuery
} = datahubApi
